<template>
  <ValidationObserver
    v-slot="{ handleSubmit, invalid }"
    ref="form"
    slim
  >
    <form
      class="form-horizontal"
      name="verifyCreditCardForm"
      novalidate
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <Field
        :label="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_FRONT')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_FRONT_TT')"
      >
        <template #element>
          <div class="col-sm-3">
            <label class="btn btn-default btn-file btn-sm">
              {{ $t('BROWSE') }}
              <input
                ref="kycFront"
                type="file"
                :accept="verifyConfig.allowedMimeTypes.join(',')"
                @change="selectKycImage($event,'front')"
              />
            </label>
          </div>
        </template>
      </Field>
      <div class="form-group">
        <div class="col-sm-offset-4 col-sm-8 col-md-9 col-md-offset-3">
          <p class="m-b-15" v-html="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_FRONT_EXAMPLE')" />
          <p v-if="frontMaxFileSize" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_MAX_FILE_SIZE_ERROR') }}</p>
          <p v-if="frontFileInvalid" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_FILE_ERROR') }}</p>
          <img v-if="selectedKycFront" :width="verifyConfig.previewSize" :src="selectedKycFront" />
          <img v-else :width="verifyConfig.previewSize" src="@/assets/images/cc_front.png" />
        </div>
      </div>
      <Field
        :label="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_BACK')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_BACK_TT')"
      >
        <template #element>
          <div class="col-sm-3">
            <label class="btn btn-default btn-file btn-sm">
              {{ $t('BROWSE') }}
              <input
                ref="kycBack"
                type="file"
                :accept="verifyConfig.allowedMimeTypes.join(',')"
                @change="selectKycImage($event,'back')"
              />
            </label>
          </div>
        </template>
      </Field>
      <div class="form-group">
        <div class="col-sm-offset-4 col-sm-8 col-md-9 col-md-offset-3">
          <p v-if="backMaxFileSize" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_MAX_FILE_SIZE_ERROR') }}</p>
          <p v-if="backFileInvalid" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_FILE_ERROR') }}</p>
          <img v-if="selectedKycBack" :width="verifyConfig.previewSize" :src="selectedKycBack" />
          <img v-else :width="verifyConfig.previewSize" src="@/assets/images/cc_back.png" />
        </div>
      </div>
      <Field
        :label="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_ID')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_UPLOAD_CARD_ID_TT')"
      >
        <template #element>
          <div class="col-sm-3">
            <label class="btn btn-default btn-file btn-sm">
              {{ $t('BROWSE') }}
              <input
                ref="kycId"
                type="file"
                :accept="verifyConfig.allowedMimeTypes.join(',')"
                @change="selectKycImage($event,'id')"
              />
            </label>
          </div>
        </template>
      </Field>
      <div class="form-group">
        <div class="col-sm-offset-4 col-sm-8 col-md-9 col-md-offset-3">
          <p v-if="idMaxFileSize" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_MAX_FILE_SIZE_ERROR') }}</p>
          <p v-if="idFileInvalid" class="text-danger m-b-20">{{ $t('ACCOUNT_BILLING_CC_VERIFY_MODAL_FILE_ERROR') }}</p>
          <img v-if="selectedKycId" :width="verifyConfig.previewSize" :src="selectedKycId" />
          <img v-else :width="verifyConfig.previewSize" src="@/assets/images/drivers_licence.png" />
        </div>
      </div>
      <div class="row display-flex flex--justify-end p-t-25">
        <PrimaryButton
          :disabled="formInvalid || isVerifyingCreditCard"
          :show-loader-on-form="isVerifyingCreditCard"
          class="p-r-25 p-l-25"
        >
          <span v-t="'ACCOUNT_BILLING_CC_VERIFY_MODAL_VERIFY_BUTTON'" />
        </PrimaryButton>
        <button
          v-t="'CANCEL'"
          class="btn btn-danger m-l-15"
          @click="onCloseModal"
        />
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import messages from '../../locales';
import sharedMessages from '@/core/locales';

export default {
  name: 'VerifyCreditCardForm',
  components: {
  },
  props: {
    selectedCard: {
      type: String,
      validator: (value) => value.length > 0
    },
  },
  data() {
    return {
      kyc: {
        front: null,
        back: null,
        id: null
      },
      frontMaxFileSize: false,
      backMaxFileSize: false,
      idMaxFileSize: false,
      frontFileInvalid: false,
      backFileInvalid: false,
      idFileInvalid: false,
      frontObjectUrl: null,
      backObjectUrl: null,
      idObjectUrl: null,
      verifyConfig: Object.freeze({
        maxFileSize: 7000000,
        allowedMimeTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
        previewSize: 230
      })
    };
  },
  computed: {
    ...mapGetters('billingSettingsStore', ['verifyCreditCardStatus']),
    selectedKycFront() {
      return this.processImageForDisplay('front');
    },
    selectedKycBack() {
      return this.processImageForDisplay('back');
    },
    selectedKycId() {
      return this.processImageForDisplay('id');
    },
    isVerifyingCreditCard() {
      return this.verifyCreditCardStatus.status === STATUS_PENDING;
    },
    formInvalid() {
      return !this.kyc?.front || !this.kyc?.back || !this.kyc?.id;
    }
  },
  beforeDestroy() {
    this.cleanObjectUrls();
  },
  methods: {
    ...mapActions('billingSettingsStore', ['verifyCreditCard','getCreditCards']),
    async onSubmit() {
      const payload = {
        kyc: this.kyc,
        creditCardId: this.selectedCard
      }
      await this.verifyCreditCard(payload);
      if (this.verifyCreditCardStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_VERIFY_SUCCESSFUL_MESSAGE'));
        this.getCreditCards();
        this.onCloseModal();
      }
      if (this.verifyCreditCardStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.verifyCreditCardStatus.error);
      }
    },
    selectKycImage(event, image) {
      // First validate the file
      const isFileSafe = this.validateFile(event.target.files[0]);
      if (!isFileSafe) {
        this[`${image}FileInvalid`] = true;
        this.kyc[image] = null;
        return;
      }
      if (event.target.files[0]?.size > this.verifyConfig.maxFileSize) {
        this[`${image}MaxFileSize`] = true;
        this[`${image}FileInvalid`] = false;
        this.kyc[image] = null;
      } else {
        this.kyc[image] = event.target.files[0];
        this[`${image}MaxFileSize`] = false;
        this[`${image}FileInvalid`] = false;
      }
    },
    processImageForDisplay(image) {
      if (!this.kyc[image]) {
        if (this[`${image}ObjectUrl`]) {
          window.URL.revokeObjectURL(this[`${image}ObjectUrl`]);
          this[`${image}ObjectUrl`] = null;
        }
        return null;
      }
      
      if (this[`${image}ObjectUrl`]) {
        window.URL.revokeObjectURL(this[`${image}ObjectUrl`]);
      }

      // Set invalid flag to false
      this[`${image}FileInvalid`] = false;

      this[`${image}ObjectUrl`] = window.URL.createObjectURL(this.kyc[image]);

      return this[`${image}ObjectUrl`];
    },
    cleanObjectUrls() {
      if (this.frontObjectUrl) {
        window.URL.revokeObjectURL(this.frontObjectUrl);
        this.frontObjectUrl = null;
      }
      if (this.backObjectUrl) {
        window.URL.revokeObjectURL(this.backObjectUrl);
        this.backObjectUrl = null;
      }
      if (this.idObjectUrl) {
        window.URL.revokeObjectURL(this.idObjectUrl);
        this.idObjectUrl = null;
      }
    },
    validateFile(file) {
      return (file instanceof Blob) && file.size > 0 && this.validateFileType(file);
    },
    validateFileType(file) {
      return this.verifyConfig.allowedMimeTypes.includes(file.type);
    },
    onCloseModal() {
      this.$emit('cancel');
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>